import { getRequest } from '@/utils/request';

export interface BannerItem {
  id?: number;
  title?: string;
  imageUrl?: string;
  announcementId?: number;
  status?: number;
  position?: number;
  positionName?: string;
  startTime?: string;
  endTime?: string;
  remark?: string;
  createTime?: string;
}

const cache = new Map<number, BannerItem[]>();
const inflight = new Map<number, Promise<BannerItem[]>>();

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

function preloadImages(urls: string[]) {
  urls.filter(Boolean).forEach((url) => {
    void preloadImage(url);
  });
}

async function loadBannerList(position: number, force = false): Promise<BannerItem[]> {
  if (!force && cache.has(position)) {
    return cache.get(position)!;
  }

  const data = await getBannerList({ position });
  const list = Array.isArray(data) ? data : [];
  cache.set(position, list);

  const urls = list
    .map((item) => item.imageUrl)
    .filter((url): url is string => !!url);

  if (urls[0]) {
    await preloadImage(urls[0]);
  }
  preloadImages(urls.slice(1));

  return list;
}

export const getBannerList = (params: { position: number }) =>
  getRequest('/system/banner/app/list', params, { hideLoading: true });

/** 获取 Banner 列表（缓存 + 首图预加载，避免轮播图出现慢） */
export function fetchBannerList(
  position: number,
  options: { force?: boolean } = {},
): Promise<BannerItem[]> {
  if (!options.force && cache.has(position)) {
    return Promise.resolve(cache.get(position)!);
  }

  const running = inflight.get(position);
  if (!options.force && running) {
    return running;
  }

  const promise = loadBannerList(position, options.force).finally(() => {
    inflight.delete(position);
  });
  inflight.set(position, promise);
  return promise;
}

/** 提前拉取并缓存 Banner，不阻塞页面 */
export function prefetchBannerList(position: number) {
  void fetchBannerList(position);
}

export function clearBannerCache(position?: number) {
  if (position == null) {
    cache.clear();
    return;
  }
  cache.delete(position);
}
